/*
 * assets/hpo-home.js
 * ──────────────────────────────────────────────────────────────────────────
 * Vanilla-JS helpers for the HpO home-page sections (US-003). These back the
 * Alpine components defined inline in the home sections so that carousel /
 * swipe / video logic is testable and faithful to the hpo-web (drinkhpo.com)
 * source — without touching the hashed Vite bundle.
 *
 * Exposed on `window.HpOHome`:
 *   - attachSwipe(el, { onLeft, onRight, threshold })  shared touch-swipe helper
 *   - registers Alpine components via `alpine:init`:
 *       hpoStats        (infinite stat carousel: desktop click-focus, mobile swipe)
 *       hpoProducts     (infinite product carousel: desktop hover bg-swap, mobile swipe)
 *       hpoReviews      (auto-rotate 5s + arrows + swipe)
 *       hpoFollowAlong  (UGC video carousel + lightbox)
 *
 * Motion constants mirror hpo-web exactly:
 *   primary transition 500ms cubic-bezier(0.645,0.045,0.355,1)
 *   video transition   600ms cubic-bezier(0.215,0.61,0.355,1)
 * ──────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // ── Shared swipe helper ────────────────────────────────────────────────
  // Only fires when horizontal motion clearly dominates vertical, so the page
  // can still scroll. Mirrors hpo-web/src/hooks/useSwipe.ts.
  function attachSwipe(el, opts) {
    if (!el) return function () {};
    var onLeft = opts.onLeft || function () {};
    var onRight = opts.onRight || function () {};
    var threshold = opts.threshold || 40;
    var startX = 0;
    var startY = 0;
    var active = false;

    function onTouchStart(e) {
      if (e.touches.length !== 1) return;
      active = true;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }
    function onTouchEnd(e) {
      if (!active) return;
      active = false;
      var t = e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) onLeft();
      else onRight();
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return function () {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  window.HpOHome = { attachSwipe: attachSwipe, mod: mod };

  // ── Alpine component registration ──────────────────────────────────────
  document.addEventListener('alpine:init', function () {
    var Alpine = window.Alpine;
    if (!Alpine) return;

    // ── Stats: infinite carousel. Desktop = click-to-focus grid (CSS-driven
    //    via desktopActive). Mobile = swipe carousel with 3 copies + silent
    //    snap back to the middle copy.
    Alpine.data('hpoStats', function (count) {
      return {
        N: count,
        position: count, // start in middle copy
        animate: true,
        snapping: false,
        desktopActive: 0,
        _snapTimer: null,
        get active() {
          return mod(this.position, this.N);
        },
        initCarousel: function () {
          var self = this;
          attachSwipe(this.$refs.track, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
        },
        prev: function () { this.animate = true; this.position--; this._scheduleSnap(); },
        next: function () { this.animate = true; this.position++; this._scheduleSnap(); },
        _scheduleSnap: function () {
          var self = this;
          if (this.snapping) return;
          if (this.position >= this.N && this.position < 2 * this.N) return;
          clearTimeout(this._snapTimer);
          this._snapTimer = setTimeout(function () {
            self.snapping = true;
            self.animate = false;
            self.position = self.N + mod(self.position, self.N);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self.animate = true;
                self.snapping = false;
              });
            });
          }, 550);
        },
      };
    });

    // ── About Values: identical interaction model to hpoStats — desktop
    //    click-to-focus 3-up grid (CSS-driven via desktopActive), mobile swipe
    //    carousel with 3 copies + silent snap back to the middle copy. Faithful
    //    to hpo-web/src/components/AboutValues.tsx (US-006).
    Alpine.data('hpoAboutValues', function (count) {
      return {
        N: count,
        position: count, // start in middle copy
        animate: true,
        snapping: false,
        desktopActive: 0,
        _snapTimer: null,
        get active() {
          return mod(this.position, this.N);
        },
        initCarousel: function () {
          var self = this;
          attachSwipe(this.$refs.track, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
        },
        prev: function () { this.animate = true; this.position--; this._scheduleSnap(); },
        next: function () { this.animate = true; this.position++; this._scheduleSnap(); },
        _scheduleSnap: function () {
          var self = this;
          if (this.snapping) return;
          if (this.position >= this.N && this.position < 2 * this.N) return;
          clearTimeout(this._snapTimer);
          this._snapTimer = setTimeout(function () {
            self.snapping = true;
            self.animate = false;
            self.position = self.N + mod(self.position, self.N);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self.animate = true;
                self.snapping = false;
              });
            });
          }, 550);
        },
      };
    });

    // ── Products: infinite carousel. Desktop hover bg-swap is handled in CSS
    //    (theme__hpo-chrome / inline) via :has(); JS drives mobile swipe + the
    //    mobile bg-layer opacity + the active-card focus state.
    Alpine.data('hpoProducts', function (count) {
      return {
        N: count,
        // Desktop: which stacked card is highlighted. Click-driven (was hover).
        // Default to the last card (Variety Pack).
        desktopActive: count > 0 ? count - 1 : 0,
        // Mobile/tablet carousel: start centered on the last card (Variety Pack),
        // matching the desktop default. Middle copy → last slot = 2N-1.
        position: count > 0 ? 2 * count - 1 : 0,
        animate: true,
        snapping: false,
        _snapTimer: null,
        get active() {
          return mod(this.position, this.N);
        },
        // mobile bg layers: lemon=idx0, orange=idx1, variety=idx2 shows both
        get showLemonMobile() { return this.active === 0 || this.active === 2; },
        get showOrangeMobile() { return this.active === 1 || this.active === 2; },
        initCarousel: function () {
          var self = this;
          attachSwipe(this.$refs.track, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
        },
        prev: function () { this.animate = true; this.position--; this._scheduleSnap(); },
        next: function () { this.animate = true; this.position++; this._scheduleSnap(); },
        _scheduleSnap: function () {
          var self = this;
          if (this.snapping) return;
          if (this.position >= this.N && this.position < 2 * this.N) return;
          clearTimeout(this._snapTimer);
          this._snapTimer = setTimeout(function () {
            self.snapping = true;
            self.animate = false;
            self.position = self.N + mod(self.position, self.N);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self.animate = true;
                self.snapping = false;
              });
            });
          }, 550);
        },
      };
    });

    // ── Reviews: auto-rotate every 5s, arrows, swipe. Slide-in animation on
    //    each change via the Web Animations API (matches hpo-web).
    Alpine.data('hpoReviews', function (count) {
      return {
        N: count,
        idx: 0,
        _timer: null,
        initCarousel: function () {
          var self = this;
          this._start();
          attachSwipe(this.$root, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
          this.$watch('idx', function () { self._animate(); });
          // initial paint
          this._animate();
        },
        _start: function () {
          var self = this;
          clearInterval(this._timer);
          this._timer = setInterval(function () {
            self.idx = mod(self.idx + 1, self.N);
          }, 5000);
        },
        _restart: function () { this._start(); },
        _animate: function () {
          var els = this.$root.querySelectorAll('[data-anim="review-card"]');
          els.forEach(function (el) {
            el.getAnimations().forEach(function (a) { a.cancel(); });
            el.animate(
              [
                { opacity: 0, transform: 'translateX(32px)' },
                { opacity: 1, transform: 'translateX(0)' },
              ],
              { duration: 500, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'both' }
            );
          });
        },
        prev: function () { this.idx = mod(this.idx - 1, this.N); this._restart(); },
        next: function () { this.idx = mod(this.idx + 1, this.N); this._restart(); },
      };
    });

    // ── FollowAlong: UGC video reel.
    //    Desktop = sliding window of 4 visible (+1 lead +1 trail), hover-play.
    //    Mobile = 3 copies, autoplay centered, click → lightbox.
    Alpine.data('hpoFollowAlong', function (count, clipsElId) {
      var DURATION = 600;
      var VISIBLE = 4;
      var GAP_DESKTOP = 16;
      var GAP_MOBILE = 8;
      var clips = [];
      try {
        var el = document.getElementById(clipsElId);
        if (el) clips = JSON.parse(el.textContent);
      } catch (e) { clips = []; }
      return {
        N: count,
        clips: clips,
        DURATION: DURATION,
        VISIBLE: VISIBLE,
        GAP_DESKTOP: GAP_DESKTOP,
        GAP_MOBILE: GAP_MOBILE,
        // desktop
        start: 0,
        offset: 0,
        animating: false,
        cardPx: 0,
        // mobile
        position: count,
        mobAnimate: true,
        snapping: false,
        _snapTimer: null,
        // lightbox
        lightboxIdx: null,
        _ro: null,
        _autoTimer: null,

        get desktopTranslateX() {
          var slot = this.cardPx + this.GAP_DESKTOP;
          return -(1 + this.offset) * slot;
        },
        // desktop track is 6 slots: lead + 4 visible + trail
        desktopMediaIdx: function (i) {
          return mod(this.start + i - 1, this.N);
        },

        initCarousel: function () {
          var self = this;
          var el = this.$refs.desktopContainer;
          if (el) {
            var update = function () {
              // Use the CONTENT width (clientWidth minus the container's own
              // horizontal padding) and the ACTUAL rendered flex gap, not the
              // hardcoded 16. The theme's root font-size is ~17.6px, so px-4/
              // gap-4 render ~17.6px; assuming 16 made the 4 cards overflow and
              // peek on both sides instead of filling the width exactly.
              var cs = getComputedStyle(el);
              var inner = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
              var trackEl = el.firstElementChild;
              var gap = trackEl ? parseFloat(getComputedStyle(trackEl).columnGap) : self.GAP_DESKTOP;
              if (gap) self.GAP_DESKTOP = gap;
              self.cardPx = (inner - (self.VISIBLE - 1) * self.GAP_DESKTOP) / self.VISIBLE;
            };
            update();
            this._ro = new ResizeObserver(update);
            this._ro.observe(el);
            // Auto-advance the desktop carousel every 3s; pause on hover so it
            // doesn't slide out from under the cursor.
            this.startAuto();
            el.addEventListener('mouseenter', function () { self.stopAuto(); });
            el.addEventListener('mouseleave', function () { self.startAuto(); });
          }
          attachSwipe(this.$refs.mobileTrack, {
            onLeft: function () { self.go(1); },
            onRight: function () { self.go(-1); },
          });
          this.$watch('position', function () { self._syncMobileVideos(); });
          this._syncMobileVideos();
        },

        // Auto-advance (desktop only). Re-armed on every advance, so a manual
        // click/swipe also resets the 3s window.
        startAuto: function () {
          var self = this;
          this.stopAuto();
          if (window.innerWidth < 1024) return;
          this._autoTimer = setInterval(function () { self.go(1); }, 3000);
        },
        stopAuto: function () {
          if (this._autoTimer) { clearInterval(this._autoTimer); this._autoTimer = null; }
        },

        go: function (dir) {
          var self = this;
          this.startAuto();
          // desktop sliding window
          if (!this.animating && this.cardPx > 0) {
            this.animating = true;
            this.offset = dir;
            setTimeout(function () {
              self.animating = false;
              self.start = self.start + dir;
              self.offset = 0;
            }, this.DURATION);
          }
          // mobile
          this.mobAnimate = true;
          this.position += dir;
          this._scheduleSnap();
        },

        _scheduleSnap: function () {
          var self = this;
          if (this.snapping) return;
          if (this.position >= this.N && this.position < 2 * this.N) return;
          clearTimeout(this._snapTimer);
          this._snapTimer = setTimeout(function () {
            self.snapping = true;
            self.mobAnimate = false;
            self.position = self.N + mod(self.position, self.N);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self.mobAnimate = true;
                self.snapping = false;
              });
            });
          }, 700);
        },

        _syncMobileVideos: function () {
          var self = this;
          var vids = this.$refs.mobileTrack
            ? this.$refs.mobileTrack.querySelectorAll('video')
            : [];
          vids.forEach(function (v, i) {
            if (i === self.position) {
              try { v.currentTime = 0; v.play().catch(function () {}); } catch (e) {}
            } else {
              try { v.pause(); v.currentTime = 0; } catch (e) {}
            }
          });
        },

        // desktop hover handlers
        hoverPlay: function (e) { e.currentTarget.play().catch(function () {}); },
        hoverStop: function (e) { e.currentTarget.pause(); e.currentTarget.currentTime = 0; },

        // mobile autoplay-ended → advance
        mobileEnded: function (i) { if (i === this.position) this.go(1); },

        openLightbox: function (i) {
          this.lightboxIdx = mod(i, this.N);
          document.body.style.overflow = 'hidden';
        },
        closeLightbox: function () {
          this.lightboxIdx = null;
          document.body.style.overflow = '';
        },
        lbPrev: function () {
          if (this.lightboxIdx === null) return;
          this.lightboxIdx = mod(this.lightboxIdx - 1, this.N);
        },
        lbNext: function () {
          if (this.lightboxIdx === null) return;
          this.lightboxIdx = mod(this.lightboxIdx + 1, this.N);
        },
      };
    });
  });
})();
